import type { SoundEventMapping, SoundVariation } from "./types.js";

/** Per-event memory for variation selection. */
export interface PickState {
  last: number;
  queue: number[];
  cursor: number;
}

export function createPickState(): PickState {
  return { last: -1, queue: [], cursor: 0 };
}

const weightOf = (variation: SoundVariation) => Math.max(0, variation.weight ?? 1);

function pickWeighted(
  variations: readonly SoundVariation[],
  exclude: number,
  random: () => number,
): number {
  const candidates = variations
    .map((variation, index) => ({ index, weight: weightOf(variation) }))
    .filter((c) => c.index !== exclude && c.weight > 0);
  const total = candidates.reduce((sum, c) => sum + c.weight, 0);
  if (total === 0) {
    // Only the previous pick is left: repeat it rather than wake a muted (weight 0) one.
    const previous = variations[exclude];
    if (previous && weightOf(previous) > 0) return exclude;
    return Math.max(
      0,
      variations.findIndex((v) => weightOf(v) > 0),
    );
  }
  let roll = random() * total;
  for (const candidate of candidates) {
    roll -= candidate.weight;
    if (roll < 0) return candidate.index;
  }
  // Unreachable while total > 0, which needs a candidate; it satisfies the index type.
  return candidates.at(-1)?.index ?? 0;
}

function swap(items: number[], a: number, b: number): void {
  const first = items[a];
  const second = items[b];
  if (first === undefined || second === undefined) return;
  items[a] = second;
  items[b] = first;
}

function nextShuffle(count: number, last: number, random: () => number): number[] {
  const order = Array.from({ length: count }, (_, i) => i);
  for (let i = count - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    swap(order, i, j);
  }
  // A new round never opens with the previous round's last pick.
  if (count > 1 && order[0] === last) swap(order, 0, 1);
  return order;
}

/**
 * Index of the variation to play. `random` is weighted and never repeats the previous pick
 * when there is a choice; `sequence` cycles in order; `shuffle` plays each once per round.
 */
export function pickVariation(
  variations: readonly SoundVariation[],
  select: SoundEventMapping["select"],
  state: PickState,
  random: () => number,
): number {
  const count = variations.length;
  let pick: number;
  if (count <= 1) {
    pick = 0;
  } else if (select === "sequence") {
    pick = (state.last + 1) % count;
  } else if (select === "shuffle") {
    if (state.cursor >= state.queue.length || state.queue.length !== count) {
      state.queue = nextShuffle(count, state.last, random);
      state.cursor = 0;
    }
    pick = state.queue[state.cursor++] ?? 0;
  } else {
    pick = pickWeighted(variations, state.last, random);
  }
  state.last = pick;
  return pick;
}

/** A playing instance of an event, as far as voice limiting cares. */
export interface ActiveVoice {
  id: number;
  startedMs: number;
}

export interface VoiceState {
  lastStartMs: number | undefined;
  voices: readonly ActiveVoice[];
}

export type Admission = { admit: false } | { admit: true; steal?: number };

/** Applies an event's cooldown, then its voice cap by stealing the oldest playing voice. */
export function admitPlay(
  limits: Pick<SoundEventMapping, "cooldownMs" | "maxVoices">,
  state: VoiceState,
  nowMs: number,
): Admission {
  if (
    limits.cooldownMs !== undefined &&
    state.lastStartMs !== undefined &&
    nowMs - state.lastStartMs < limits.cooldownMs
  ) {
    return { admit: false };
  }
  if (limits.maxVoices !== undefined && state.voices.length >= limits.maxVoices) {
    // A cap of 0 (a bad manifest value) has nothing to steal: skip the sound.
    if (state.voices.length === 0) return { admit: false };
    const oldest = state.voices.reduce((a, b) => (b.startedMs < a.startedMs ? b : a));
    return { admit: true, steal: oldest.id };
  }
  return { admit: true };
}
